import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import axios from "axios";
import { API } from "../App";

export default function AdminIntelligencePage({ user }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [daily, setDaily] = useState([]);
  const [features, setFeatures] = useState([]);
  const [topQuestions, setTopQuestions] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [feedbackSummary, setFeedbackSummary] = useState({ helpful: 0, unclear: 0, not_helpful: 0 });
  const [synonymSuggestions, setSynonymSuggestions] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAllData(); }, []);

  async function loadAllData() {
    setLoading(true);
    try {
      const [ov, dy, ft, topQ, unans, fbSum, syns, recent] = await Promise.all([
        axios.get(`${API}/analytics/dashboard/overview`).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/dashboard/daily-trends`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/dashboard/feature-usage`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/intelligence/top-questions`).catch(() => ({ data: { questions: [] } })),
        axios.get(`${API}/admin/intelligence/unanswered`).catch(() => ({ data: { questions: [] } })),
        axios.get(`${API}/admin/intelligence/feedback-summary`).catch(() => ({ data: { summary: {} } })),
        axios.get(`${API}/admin/learning/synonym-suggestions`).catch(() => ({ data: { suggestions: [] } })),
        axios.get(`${API}/analytics/dashboard/recent-events?limit=15`).catch(() => ({ data: [] })),
      ]);
      setOverview(ov.data);
      setDaily(Array.isArray(dy.data) ? dy.data : []);
      setFeatures(Array.isArray(ft.data) ? ft.data : []);
      setTopQuestions(topQ.data.questions || []);
      setUnanswered(unans.data.questions || []);
      setFeedbackSummary(fbSum.data.summary || {});
      setSynonymSuggestions(syns.data.suggestions || []);
      setRecentEvents(Array.isArray(recent.data) ? recent.data : []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  const totalFeedback = (feedbackSummary.helpful || 0) + (feedbackSummary.unclear || 0) + (feedbackSummary.not_helpful || 0);
  const maxDailyCount = daily.length ? Math.max(...daily.map(d => d.events || d.count || 0), 1) : 1;

  function exportCSV() {
    let csv = "Category,Metric,Value\n";

    // Overview stats
    if (overview) {
      csv += `Overview,Total Events,${overview.totalEvents ?? 0}\n`;
      csv += `Overview,Active Today,${overview.activeUsersToday ?? 0}\n`;
      csv += `Overview,Total Sessions,${overview.totalSessions ?? 0}\n`;
      csv += `Overview,Top Feature,${overview.topFeature ?? "N/A"}\n`;
    }

    // Feature usage
    features.forEach(f => {
      csv += `Feature Usage,${f.feature || f._id || "Unknown"},${f.count || f.events || 0}\n`;
    });

    // Top questions
    topQuestions.forEach((q, i) => {
      csv += `Top Questions,#${i + 1}: ${(q.text || q.question || "").replace(/,/g, ";")},${q.count || 0}\n`;
    });

    // Daily trends
    daily.forEach(d => {
      csv += `Daily Trend,${d.date || d._id || ""},${d.events || d.count || 0}\n`;
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `askstewie-analytics-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportPDF() {
    const date = new Date().toLocaleDateString();
    const time = new Date().toLocaleTimeString();

    const featureRows = features.map(f =>
      `<tr><td>${f.feature || f._id || "Unknown"}</td><td style="text-align:right;font-weight:600">${f.count || f.events || 0}</td></tr>`
    ).join("");

    const questionRows = topQuestions.slice(0, 10).map((q, i) =>
      `<tr><td>${i + 1}</td><td>${q.text || q.question || ""}</td><td style="text-align:right">${q.count || 0}</td></tr>`
    ).join("");

    const html = `<!DOCTYPE html><html><head><title>AskStewie Analytics Report</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1e293b;max-width:800px;margin:0 auto;padding:40px 20px;line-height:1.6}
      .header{text-align:center;border-bottom:2px solid #1e293b;padding-bottom:20px;margin-bottom:30px}
      .header h1{font-size:22px;margin-bottom:4px} .header .sub{color:#64748b;font-size:13px}
      .stats{display:flex;flex-wrap:wrap;gap:16px;margin-bottom:30px}
      .stat{flex:1;min-width:140px;background:#f1f5f9;padding:14px;border-radius:8px;text-align:center}
      .stat .val{font-size:24px;font-weight:700;color:#0f172a} .stat .lbl{font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px}
      h2{font-size:16px;margin:24px 0 12px;border-bottom:1px solid #e2e8f0;padding-bottom:8px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px}
      th,td{padding:8px 12px;text-align:left;border-bottom:1px solid #e2e8f0}
      th{background:#f8fafc;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b}
      .footer{text-align:center;margin-top:40px;font-size:11px;color:#94a3b8}
      @media print{body{padding:20px}}
    </style></head><body>
      <div class="header">
        <h1>AskStewie Analytics Report</h1>
        <div class="sub">Generated: ${date} at ${time}</div>
      </div>
      <div class="stats">
        <div class="stat"><div class="val">${overview?.totalEvents ?? 0}</div><div class="lbl">Total Events</div></div>
        <div class="stat"><div class="val">${overview?.activeUsersToday ?? 0}</div><div class="lbl">Active Today</div></div>
        <div class="stat"><div class="val">${overview?.totalSessions ?? 0}</div><div class="lbl">Sessions</div></div>
        <div class="stat"><div class="val">${overview?.topFeature ?? "N/A"}</div><div class="lbl">Top Feature</div></div>
      </div>
      <h2>Feature Usage</h2>
      <table><thead><tr><th>Feature</th><th style="text-align:right">Events</th></tr></thead><tbody>${featureRows || "<tr><td colspan='2'>No data</td></tr>"}</tbody></table>
      <h2>Top Questions</h2>
      <table><thead><tr><th>#</th><th>Question</th><th style="text-align:right">Count</th></tr></thead><tbody>${questionRows || "<tr><td colspan='3'>No data</td></tr>"}</tbody></table>
      <div class="footer">AskStewie Admin Intelligence &bull; For internal use only</div>
    </body></html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  }

  return (
    <MainLayout
      title="Admin Intelligence"
      subtitle="Real-time analytics, activity, and system visibility."
      user={user}
      actions={
        <div className="flex gap-3 flex-wrap">
          <button onClick={exportCSV} className="btn-secondary-dark" data-testid="export-csv-btn">
            CSV
          </button>
          <button onClick={exportPDF} className="btn-secondary-dark" data-testid="export-pdf-btn">
            PDF
          </button>
          <button onClick={loadAllData} className="btn-secondary-dark" data-testid="refresh-btn">
            Refresh
          </button>
          <button onClick={() => navigate("/")} className="btn-secondary-dark" data-testid="back-home-btn">
            &larr; Home
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="text-center py-12" style={{ color: "var(--text-muted)" }}>Loading intelligence data...</div>
      ) : (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="overview-stats">
            {[
              { label: "Total Events", value: overview?.totalEvents ?? 0, color: "var(--color-accent)" },
              { label: "Active Today", value: overview?.activeUsersToday ?? 0, color: "var(--color-accent)" },
              { label: "Total Sessions", value: overview?.totalSessions ?? 0, color: "#22c55e" },
              { label: "Top Feature", value: overview?.topFeature ?? "N/A", color: "#f59e0b", isText: true },
              { label: "Avg Session", value: overview?.avgSessionDuration ? `${Math.round(overview.avgSessionDuration)}s` : "0s", color: "#a78bfa", isText: true },
            ].map((s) => (
              <div key={s.label} className="card-dark p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{s.label}</p>
                <h3 className={`${s.isText ? "text-lg" : "text-3xl"} font-bold mt-1`} style={{ color: s.color }}>{s.value}</h3>
              </div>
            ))}
          </div>

          {/* Daily Usage Chart */}
          <div className="card-dark p-6 mt-6" data-testid="daily-usage-chart">
            <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Daily Usage (14 days)</h3>
            {daily.length > 0 ? (
              <div className="flex items-end gap-1" style={{ height: 120 }}>
                {daily.map((d, i) => {
                  const count = d.events || d.count || 0;
                  return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${count} events`}>
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>{count}</span>
                    <div
                      className="w-full rounded-t-md transition-all"
                      style={{
                        height: `${Math.max((count / maxDailyCount) * 90, 4)}px`,
                        background: "var(--color-accent)",
                        opacity: 0.7 + (0.3 * (count / maxDailyCount)),
                      }}
                    />
                    <span className="text-xs" style={{ color: "var(--text-muted)", fontSize: 9 }}>
                      {d.date.slice(5)}
                    </span>
                  </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-center py-6" style={{ color: "var(--text-muted)" }}>
                No usage data yet. Events are tracked when users interact with features.
              </p>
            )}
          </div>

          {/* Feature Breakdown + Feedback Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Feature Breakdown */}
            <div className="card-dark p-6" data-testid="feature-breakdown">
              <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Feature Breakdown</h3>
              {features.length > 0 ? (
                <div className="space-y-3">
                  {features.map((f) => {
                    const maxCount = features[0]?.count || 1;
                    return (
                      <div key={f.feature} className="flex items-center gap-3">
                        <span className="text-sm w-28 truncate" style={{ color: "var(--text-secondary)" }}>{f.feature}</span>
                        <div className="flex-1 h-6 rounded-lg overflow-hidden" style={{ background: "var(--surface-card-hover)" }}>
                          <div
                            className="h-full rounded-lg"
                            style={{
                              width: `${(f.count / maxCount) * 100}%`,
                              background: "var(--color-accent)",
                              opacity: 0.8,
                            }}
                          />
                        </div>
                        <span className="text-sm font-bold w-10 text-right" style={{ color: "var(--text-primary)" }}>{f.count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-center py-4" style={{ color: "var(--text-muted)" }}>No feature data yet.</p>
              )}
            </div>

            {/* Feedback Summary */}
            <div className="card-dark p-6" data-testid="feedback-summary">
              <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Answer Feedback ({totalFeedback} total)</h3>
              <div className="space-y-3">
                {[
                  { label: "Helpful", value: feedbackSummary.helpful || 0, color: "#22c55e" },
                  { label: "Unclear", value: feedbackSummary.unclear || 0, color: "#f59e0b" },
                  { label: "Not Helpful", value: feedbackSummary.not_helpful || 0, color: "#ef4444" },
                ].map((fb) => (
                  <div key={fb.label} className="flex items-center justify-between p-3 rounded-xl" style={{ background: "var(--surface-card-hover)", border: "1px solid var(--border-default)" }}>
                    <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{fb.label}</span>
                    <span className="text-xl font-bold" style={{ color: fb.color }}>{fb.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Questions + Unanswered */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="card-dark p-6" data-testid="top-questions-panel">
              <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Top Questions</h3>
              <div className="space-y-2">
                {topQuestions.length > 0 ? topQuestions.slice(0, 10).map((q, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl" style={{ background: "var(--surface-card-hover)", border: "1px solid var(--border-default)" }}>
                    <p className="text-sm flex-1" style={{ color: "var(--text-secondary)" }}>{q.question || q}</p>
                    {q.count && (
                      <span className="ml-3 text-xs font-semibold px-2 py-1 rounded-lg" style={{ background: "var(--color-accent-soft)", color: "var(--color-accent)" }}>
                        {q.count}x
                      </span>
                    )}
                  </div>
                )) : (
                  <p className="text-sm text-center py-4" style={{ color: "var(--text-muted)" }}>No questions logged yet.</p>
                )}
              </div>
            </div>

            <div className="card-dark p-6" data-testid="unanswered-panel">
              <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Weak / Unanswered Questions</h3>
              <div className="space-y-2">
                {unanswered.length > 0 ? unanswered.slice(0, 10).map((q, i) => (
                  <div key={i} className="p-3 rounded-xl" style={{ background: "var(--surface-card-hover)", border: "1px solid var(--border-default)" }}>
                    <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{q.question || q}</p>
                    {q.confidence && <span className="text-xs mt-1 block" style={{ color: "#ef4444" }}>Confidence: {q.confidence}</span>}
                  </div>
                )) : (
                  <p className="text-sm text-center py-4" style={{ color: "var(--text-muted)" }}>No weak answers logged.</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Events */}
          <div className="card-dark p-6 mt-6" data-testid="recent-events">
            <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Recent Events</h3>
            {recentEvents.length > 0 ? (
              <div className="space-y-2">
                {recentEvents.map((ev, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-xl" style={{ background: "var(--surface-card-hover)", border: "1px solid var(--border-default)" }}>
                    <span className="app-badge">{ev.eventType || ev.event_type}</span>
                    <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{ev.feature}</span>
                    {ev.userName && ev.userName !== "Unknown" && (
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>{ev.userName}</span>
                    )}
                    <span className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>
                      {(ev.createdAt || ev.created_at) ? new Date(ev.createdAt || ev.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-center py-4" style={{ color: "var(--text-muted)" }}>
                No events tracked yet. Interact with Ask Stewie, Job Briefing, or Fact Finder to generate data.
              </p>
            )}
          </div>

          {/* Synonym Suggestions */}
          <div className="card-dark p-6 mt-6" data-testid="synonym-panel">
            <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Synonym Suggestions</h3>
            <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
              Auto-generated based on weak answer patterns to improve retrieval accuracy.
            </p>
            {synonymSuggestions.length > 0 ? (
              <div className="space-y-2">
                {synonymSuggestions.map((s, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-xl" style={{ background: "var(--surface-card-hover)", border: "1px solid var(--border-default)" }}>
                    <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{s.term || s.original}</span>
                    <span style={{ color: "var(--text-muted)" }}>&rarr;</span>
                    <span className="text-sm" style={{ color: "var(--color-accent)" }}>{(s.synonyms || s.suggestions || []).join(", ")}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-center py-4" style={{ color: "var(--text-muted)" }}>No suggestions generated yet.</p>
            )}
          </div>
        </>
      )}
    </MainLayout>
  );
}
